import { CommonModule } from '@angular/common';
import { Component, Input, input, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormsModule, FormGroup, FormArray, FormBuilder, FormControl } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DialogService, DynamicDialogConfig, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { ReferentialsPrestationsService } from '../referentials-prestations.service';
import { Prestation } from '../prestation.model';
import { Subject, takeUntil } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PrestationContratsAssocies } from './prestation--contrats-associes.model';
import { HttpErrorResponse } from '@angular/common/http';
import { GenericErrorResponse, GenericResponse } from '../../../../../core/models/generic-response.model';
import { ToastModule } from 'primeng/toast';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-contrats-associes',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule, InputTextModule, InputGroupModule, InputGroupAddonModule, ConfirmDialogModule, ReactiveFormsModule, ToggleButtonModule, FormsModule, TableModule, CheckboxModule, ConfirmDialogModule, ToastModule, TranslocoModule],
  providers: [ConfirmationService, MessageService],
  templateUrl: './contrats-associes.component.html',
  styleUrl: './contrats-associes.component.css',
})
export class ContratsAssociesComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();

  errors: GenericErrorResponse[] = [];

  prestationId?: number;
  prestation: Prestation[] = [];

  visible: boolean = false;
  selectedContrats!: FormBuilder[];
  checked: boolean = true;
  loading: boolean = false;

  contratsAssociesForm = this.formBuilder.group({
    contratsArray: this.formBuilder.array([]),
  });

  userHasReadWritePermission: boolean = false;
  prestatContratAssocies!: PrestationContratsAssocies;

  constructor(
    private formBuilder: FormBuilder,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private prestationService: ReferentialsPrestationsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    if (!!this.config.data) {
      this.prestationId = this.config.data.prestationId;
      this.userHasReadWritePermission = this.config.data.userHasReadWritePermission;
    }

    if (!!this.prestationId) {
      this.loadContratsAssocies(this.prestationId);
    }
  }

  public loadContratsAssocies(selectedPrestation: number) {
    this.fetchContratsAssocies(selectedPrestation);
  }

  public fetchContratsAssocies(selectedPrestationId: number) {
    this.loading = true;
    this.prestationService
      .getContratsAssocies(selectedPrestationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (prestatContratAssocie) => {
          this.prestatContratAssocies = prestatContratAssocie;
          this.prestatContratAssocies.contrats.forEach((contratLine) => {
            if (this.contratsFormArray) {
              const contratAssociee = this.prestatContratAssocies.prestationRequest.contratIds.filter((contratIds) => contratIds === contratLine.id);
              let isCheck = contratAssociee.length == 1;
              this.contratsFormArray.push(
                this.formBuilder.group({
                  id: [contratLine.id],
                  associated: [isCheck],
                  libelle: [contratLine.libelle],
                  entiteGestion: [contratLine.entiteGestion],
                }),
              );
            }
          });
        },
        error: (error: HttpErrorResponse) => this.handleHttpErrorResponse(error),
      });
  }

  get contratsFormArray() {
    return this.contratsAssociesForm.controls['contratsArray'] as FormArray;
  }

  private handleHttpErrorResponse(error: HttpErrorResponse) {
    this.errors = [];
    this.errors.push({
      errorInputId: error.status.toString(),
      errorCode: error.status.toString(),
      errorMsg: error.statusText,
    } as GenericErrorResponse);
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
  }

  saveContratAssocie() {
    let request = {
      prestationId: this.prestationId,
      contratIds: this.convertContratIds(),
    };
    this.prestationService
      .saveContratAssocies(JSON.stringify(request))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (genericResponse) => {
          this.handleResponse(genericResponse);
        },
        error: (error: HttpErrorResponse) => {
          this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
        },
      });
  }

  private convertContratIds(): number[] {
    return this.contratsFormArray.controls
      .filter((control) => {
        let checked: boolean = (control as FormGroup).controls.associated.value;
        return checked;
      })
      .map((control) => {
        let contratId: number = (control as FormGroup).controls.id.value;
        return contratId;
      });
  }

  handleResponse(genericResponse: GenericResponse) {
    if (genericResponse.success) {
      this.messageService.add({ severity: 'info', summary: this.translocoService.translate('summary.success'), detail: genericResponse.message });
      this.close(true);
    } else {
      this.errors = genericResponse.errors.length > 0 ? genericResponse.errors : [genericResponse.exception];
      this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue ' + this.errors.map((e) => e.errorMsg), life: 3000 });
    }
  }

  close(result: boolean): void {
    this.ref.close(result);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
